import React, { useMemo, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import API_ENDPOINTS from '../ApiEndPoints.jsx';

export default function TemplateImportWizard() {
  const [zipFile, setZipFile] = useState(null);
  const [force, setForce] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(() => {
    return !!zipFile && !isSubmitting;
  }, [zipFile, isSubmitting]);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!zipFile) {
      setError('Please choose a .zip file.');
      return;
    }
    if (!String(zipFile.name || '').toLowerCase().endsWith('.zip')) {
      setError('File must be a .zip');
      return;
    }

    setIsSubmitting(true);
    try {
      const form = new FormData();
      form.append('zip', zipFile);
      form.append('force', force ? '1' : '0');

      const res = await axios.post(API_ENDPOINTS.webTemplates.import, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(res.data);
      if (!res.data?.success) {
        setError(res.data?.error || 'Import failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Request failed';
      setError(msg);
      setResult(err?.response?.data || null);
    } finally {
      setIsSubmitting(false);
    }
  }

  const summary = result?.summary || null;
  const logs = Array.isArray(result?.logs) ? result.logs : [];
  const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
  const assetsMd = typeof result?.assets_to_upload_md === 'string' ? result.assets_to_upload_md : '';

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <h1>Web Template Import Wizard</h1>
          <p className="text-muted">
            Upload a single ZIP export (manifest + assets + pages). Super Admins only.
          </p>

          {error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="well">
            <div className="form-group">
              <label>Template ZIP (.zip)</label>
              <input
                type="file"
                className="form-control"
                accept=".zip,application/zip"
                onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                disabled={isSubmitting}
              />
            </div>

            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  disabled={isSubmitting}
                />{' '}
                Force overwrite existing TIDs
              </label>
            </div>

            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {isSubmitting ? 'Importing…' : 'Import'}
            </button>
          </form>

          {summary ? (
            <div className="panel panel-default">
              <div className="panel-heading">Summary</div>
              <div className="panel-body">
                <div className="row">
                  <div className="col-sm-6">
                    <ul className="list-unstyled">
                      <li><strong>Assets</strong>: {summary.assets?.uploaded || 0} uploaded, {summary.assets?.missing || 0} missing</li>
                      <li><strong>Components</strong>: {summary.components?.created || 0} created, {summary.components?.updated || 0} updated</li>
                      <li><strong>Templates</strong>: {summary.templates?.created || 0} created, {summary.templates?.updated || 0} updated</li>
                      <li><strong>Pages</strong>: {summary.pages?.created || 0} created, {summary.pages?.updated || 0} updated</li>
                    </ul>
                  </div>
                  <div className="col-sm-6">
                    {warnings.length ? (
                      <div className="alert alert-warning" role="alert">
                        <strong>Warnings</strong>
                        <ul style={{ marginBottom: 0 }}>
                          {warnings.slice(0, 20).map((w, idx) => (
                            <li key={idx}>{w}</li>
                          ))}
                        </ul>
                        {warnings.length > 20 ? <div>({warnings.length - 20} more…)</div> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {logs.length ? (
            <div className="panel panel-default">
              <div className="panel-heading">Logs</div>
              <div className="panel-body">
                <pre style={{ maxHeight: 320, overflow: 'auto' }}>
                  {logs.map((l) => `[${l.phase}] ${l.message}`).join('\n')}
                </pre>
              </div>
            </div>
          ) : null}

          {assetsMd ? (
            <div className="panel panel-default">
              <div className="panel-heading">assets_to_upload.md</div>
              <div className="panel-body">
                <div style={{ maxHeight: 320, overflow: 'auto' }}>
                  <ReactMarkdown>{assetsMd}</ReactMarkdown>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
